/**
 * Returns the list of months spanning `startDate` to `endDate`, inclusive.
 * Defaults to the current month through the same month a year later, so the
 * default range is 12 months.
 *
 * Example (if current month is June 2026):
 * [
 *   "Июнь 2026",
 *   "Июль 2026",
 *   "Август 2026",
 *   ...
 *   "Май 2027"
 * ]
 *
 * Month names are generated using the Russian locale (`ru-RU`).
 *
 * @param {string|Date} [startDate] - Anchors the first month. Defaults to today.
 * @param {string|Date} [endDate] - Anchors the last month. Defaults to 12 months after `startDate`.
 * @returns {string[]} Array of month-year labels covering the range.
 */
export const getUpcomingMonths = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : new Date();
  const startAnchor = new Date(start.getFullYear(), start.getMonth(), 1);

  const end = endDate ? new Date(endDate) : new Date(startAnchor.getFullYear(), startAnchor.getMonth() + 11, 1);
  const endAnchor = new Date(end.getFullYear(), end.getMonth(), 1);

  const monthsCount = Math.max(
    1,
    (endAnchor.getFullYear() - startAnchor.getFullYear()) * 12 + (endAnchor.getMonth() - startAnchor.getMonth()) + 1
  );

  return Array.from({ length: monthsCount }, (_, i) => {
    const date = new Date(
      startAnchor.getFullYear(),
      startAnchor.getMonth() + i,
      1
    );

    const month = date.toLocaleString('ru-RU', {
      month: 'long',
    });

    return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getFullYear()}`;
  });
};

/**
 * Convert a datetime string (ISO-like) into a human-readable
 * date formatted as `DD.MM.YYYY`.
 *
 * @param {string} dateStr - The input date string, e.g. "2025-12-31 00:00:00".
 * @returns {string} Formatted date or an empty string when input is falsy.
 */
export function formatDate(dateStr) {
    if (!dateStr) return "";

    const [datePart] = dateStr.split(" ");
    const [year, month, day] = datePart.split("-");

    return `${day}.${month}.${year}`;
}

// Orders
// export const ordersStatusOptions = ['Completed', 'In transit', 'Created'];

/**
 * Return a colour code associated with an order status. Used for
 * displaying status badges with consistent colouring.
 *
 * @param {string} status - The status string (case-insensitive).
 * @returns {string} Hex colour string; defaults to black for unknown.
 */
export function getOrdersStatusColor(status) {
    const lowerStatus = status?.toLowerCase();

    switch (lowerStatus) {
        case 'completed':
        case 'завершен':
            return '#25A854';
        case 'in transit':
        case 'в транзите':
            return '#FFA500';
        case 'created':
        case 'создан':
            return '#9A9A9A';
        default:
            return '#000000';
    }
}

/**
 * Formats the date string (e.g., '2025-12-16') into a short month name
 * Example output: "Dec"
 *
 * @param {string} dateStr - ISO-compatible date string.
 * @returns {string} Formatted date or empty string when input is falsy.
 */
export function formatToShortMonth (dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);
    const month = date.toLocaleDateString("ru-RU", { month: "short" }).replace('.', '');
    return month.charAt(0).toUpperCase() + month.slice(1);
};

/**
 * Formats the date string (e.g., '2025-12-16') into a day with short month name
 * Example output: "Dec 28"
 *
 * @param {string} dateStr - ISO-compatible date string.
 * @returns {string} Formatted date or empty string when input is falsy.
 */
export function formatToMonthDay (dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);
    const month = date.toLocaleDateString("ru-RU", { month: "short" }).replace('.', '');
    return month.charAt(0).toUpperCase() + month.slice(1) + " " + date.getDate();
};

/**
 * Format a date string (e.g., '2025-12-16') into a short human-readable form used in filters.
 * Example output: "Фев 5, 2026".
 *
 * @param {string} dateStr - ISO-compatible date string.
 * @returns {string} Formatted date or empty string when input is falsy.
 */
export function formatToReadableDate(dateStr) {
    if (!dateStr) return "";

    const monthNames = [
        "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
        "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"
    ];

    const parts = dateStr.split('-');
    const month = monthNames[parseInt(parts[1]) - 1];
    const year = parts[0];

    if (parts.length === 2) {
        return `${month}, ${year}`;
    }

    const day = parseInt(parts[2]);
    return `${month} ${day}, ${year}`;
};

/**
 * Format a date string (e.g., '2025-12-16') into a short abbreviated form.
 * Example output: "Фев'2026".
 *
 * @param {string} dateStr - ISO-compatible date string.
 * @returns {string} Formatted date or empty string when input is falsy.
 */
export function formatToAbbrevMonthYear(dateStr) {
    if (!dateStr) return "";

    const monthNames = [
        "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
        "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"
    ];

    const parts = dateStr.split('-');
    const month = monthNames[parseInt(parts[1]) - 1];
    const year = parts[0].slice(-2); 

    if (parts.length === 2) {
        return `${month} ${year}`;
    }

    return `${month} ${year}`;
};

/**
 * Convert a JavaScript `Date` object into an `YYYY-MM-DD` string
 * suitable for `<input type="date">` value attributes.
 *
 * @param {Date} date - The date to format.
 * @returns {string} Formatted date or placeholder when falsy.
 */
export function formatIntoIsoDate(date) {
    if (!date) return "--.--.----";
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
};

/**
 * Format a `Date` object to `YYYY-MM` string. Useful for
 * `<input type="month">` components.
 *
 * @param {Date} date - Date instance to convert.
 * @returns {string} Year-month string or empty if input falsy.
 */
export function formatInputDateMonth(date) {
    if (!date) return "";

    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}`;
};

/**
 * Merge two data arrays for the dashboard chart: historical and forecast
 * sales summaries. Ensures each date has a single entry with all
 * available metrics and interpolates the boundary between history and
 * forecast.
 *
 * @param {Array<Object>} aggregated_historical_sales_data - Historical
 *   records containing `date`, `total_fact_value`, `total_target_value` etc.
 * @param {Array<Object>} aggregated_forecast_sales_data - Forecast
 *   records containing `date`, `total_baseline_forecast_value`, etc.
 * @returns {Array<Object>} Sorted array of merged records ready for plots.
 */
export function mergePlotDataDashboard(
    aggregated_historical_sales_data,
    aggregated_forecast_sales_data
) {
    const map = new Map();

    // historical
    aggregated_historical_sales_data.forEach(item => {
        map.set(item.date, {
            date: item.date,
            factual: item.total_fact_value ?? null,
            target: item.total_target_value ?? null,
            stock: item.total_past_available_stock ?? null,
            hub_stock: item.total_past_hub_stock ?? null,
            forecast: null,
            adjustedForecast: null,
        });
    });

    // forecast
    aggregated_forecast_sales_data.forEach(item => {
        if (!map.has(item.date)) {
            map.set(item.date, {
                date: item.date,
                factual: null,
                target: null,
                stock: item.total_future_available_stock ?? null,
                hub_stock: item.total_future_hub_stock ?? null,
                forecast: item.total_baseline_forecast_value ?? null,
                adjustedForecast: item.total_adjusted_forecast_value ?? null,
            });
        } else {
            // same date exists → enrich it
            const existing = map.get(item.date);
            map.set(item.date, {
                ...existing,
                stock: item.total_future_available_stock ?? existing.stock,
                forecast: item.total_baseline_forecast_value ?? null,
                adjustedForecast: item.total_adjusted_forecast_value ?? null,
            });
        }
    });

    const result = Array.from(map.values()).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );

    for (let i = 0; i < result.length - 1; i++) {
        const current = result[i];
        const next = result[i + 1];

        const isHistoricalEnd =
            current.factual != null &&
            current.target != null &&
            current.forecast == null &&
            current.adjustedForecast == null;

        const isForecastStart =
            next.forecast != null &&
            next.adjustedForecast != null &&
            next.factual == null &&
            next.target == null;

        if (isHistoricalEnd && isForecastStart) {
            current.forecast = current.factual;
            current.adjustedForecast = current.target;
        }
    }

    return result;
}

/**
 * Combine historical and forecast data tables keyed by period. Similar to
 * `mergePlotDataDashboard` but works on the raw table format used in
 * earlier components. Fills missing dates and carries over the last
 * historical value into the first forecast point.
 *
 * @param {Array<Object>} historical_table - Rows with `period`, `fact_value`,
 *   `target_value`, etc.
 * @param {Array<Object>} forecast_table - Rows with `period`,
 *   `baseline_forecast_value`, etc.
 * @returns {Array<Object>} Chronologically sorted merged array.
 */
export function mergePlotData(historical_table, forecast_table) {
    const map = new Map();

    historical_table.forEach(item => {
        map.set(item.period, {
            date: item.period,
            factual: item.fact_value ?? null,
            target: item.target_value ?? null,
            stock: item.past_available_stock ?? null,
            hub_stock: item.past_hub_stock ?? null,
            forecast: null,
            adjustedForecast: null,
        });
    });

    forecast_table.forEach(item => {
        if (!map.has(item.period)) {
            map.set(item.period, {
                date: item.period,
                factual: null,
                target: null,
                stock: item.future_available_stock ?? null,
                hub_stock: item.future_hub_stock ?? null,
                forecast: item.baseline_forecast_value ?? null,
                adjustedForecast: item.adjusted_forecast_value ?? null,
            });
        } else {
            const existing = map.get(item.period);
            map.set(item.period, {
                ...existing,
                stock: item.future_available_stock ?? existing.stock,
                forecast: item.baseline_forecast_value ?? null,
            });
        }
    });

    const result = Array.from(map.values()).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );

    for (let i = 0; i < result.length - 1; i++) {
        const current = result[i];
        const next = result[i + 1];

        const isHistoricalEnd =
            current.factual != null &&
            current.target != null &&
            current.forecast == null;

        const isForecastStart =
            next.forecast != null &&
            next.factual == null &&
            next.target == null;

        if (isHistoricalEnd && isForecastStart) {
            current.forecast = current.factual;
            current.adjustedForecast = current.target;
        }
    }

    return result;
}

/**
 * Compute the start and end dates representing a one-month window
 * ending on the provided date.
 *
 * @param {string} dateStr - End date in `YYYY-MM-DD` format.
 * @returns {{startDate: string, endDate: string}|undefined} Object with
 *   ISO date strings or `undefined` for falsy input.
 */
export function getOneMonthRange(dateStr) {
    if (!dateStr) return
    const endDate = new Date(dateStr);
    const startDate = new Date(endDate);

    startDate.setMonth(startDate.getMonth() - 1);

    const format = (d) => d.toISOString().slice(0, 10);

    return {
        startDate: format(startDate),
        endDate: format(endDate),
    };
}

/**
 * Return the ISO date string representing the same day one calendar month
 * before the given date.
 *
 * @param {string} dateStr - Input date in `YYYY-MM-DD` format.
 * @returns {string|undefined} Prior-month date string or `undefined`.
 */
export function getOneMonthPrior(dateStr) {
    if (!dateStr) return
    const date = new Date(dateStr);
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().slice(0, 10);
}


/**
 * Format a numeric value into human-readable "kilo" units (тыс/млн/млрд).
 *
 * @param {number} value - The number to format.
 * @param {object} [options]
 * @param {number} [options.kiloThreshold=100_000] - Threshold above which to show тыс.
 * @param {number} [options.precision=0] - Decimal places to round to.
 * @param {object} [options.unitStyle] - Inline style for the unit suffix. When provided,
 *   returns JSX with the suffix in a styled `<span>`; otherwise returns a plain string.
 * @returns {string|JSX.Element} Formatted value, or "0" if invalid.
 */
export function numberToKiloFormat(value, { kiloThreshold = 100_000, precision = 0, unitStyle } = {}) {
    if (!Number.isFinite(value) || value === 0) return '0';

    const format = (val, decimals = precision) =>
        Intl.NumberFormat('ru-RU').format(parseFloat(val.toFixed(decimals)));

    const abs = Math.abs(value);
    let num, unit;
    if (abs >= 1_000_000_000)      { num = format(value / 1_000_000_000, 1); unit = 'млрд'; }
    else if (abs >= 1_000_000)     { num = format(value / 1_000_000);        unit = 'млн';  }
    else if (abs >= kiloThreshold) { num = format(value / 1_000);            unit = 'тыс';  }
    else return format(value);

    return unitStyle
        ? <span style={{ whiteSpace: 'nowrap' }}>{num}&nbsp;<span style={unitStyle}>{unit}</span></span>
        : `${num} ${unit}`;
}

/**
 * Determines the health status of a branch based on its health index.
 *
 * @param {number} healthIndex - The branch health index value.
 * @returns {'healthy' | 'normal' | 'critical' | ''} The corresponding health status:
 *  - `healthy`  → index is between 90 (inclusive) and 110 (exclusive)
 *  - `normal`   → index is between 70–90 or 110–130 (both inclusive/exclusive respectively)
 *  - `critical` → index is below 70 or 130 and above
 *  - `''`       → fallback (should not occur if index is a valid number)
 */
export const getBranchHealthStatus = (healthIndex) => {
  if (healthIndex >= 90  && healthIndex < 110) return 'healthy';
  if ((healthIndex >= 70 && healthIndex < 90) || (healthIndex >= 110 && healthIndex < 130)) return 'normal';
  if (healthIndex < 70   || healthIndex >= 130) return 'critical';
  return '';
};

/**
 * Returns the display label for a branch health status based on its health index.
 *
 * @param {number} healthIndex - The branch health index value.
 * @returns {'Здоровый' | 'Нормальный' | 'Критический' | ''} The corresponding display label:
 *  - `Здоровый`    → index is between 90 (inclusive) and 110 (exclusive)
 *  - `Нормальный`  → index is between 70–90 or 110–130 (both inclusive/exclusive respectively)
 *  - `Критический` → index is below 70 or 130 and above
 *  - `''`          → fallback (should not occur if index is a valid number)
 */
export const getBranchHealthLabel = (healthIndex) => {
  if (healthIndex >= 90  && healthIndex < 110) return 'Здоровый';
  if ((healthIndex >= 70 && healthIndex < 90) || (healthIndex >= 110 && healthIndex < 130)) return 'Нормальный';
  if (healthIndex < 70   || healthIndex >= 130) return 'Критический';
  return '';
};

// ─── Promo calendar date / week helpers ─────────────────────────────────────

/**
 * Default number of week columns in the promo calendar (12 months × 4 weeks),
 * used when no explicit date range is given.
 */
export const PROMO_TOTAL_WEEKS = 12 * 4;

/**
 * Parse a "DD.MM.YYYY" date string (the promo endpoint's format) into a Date.
 * Falls back to native parsing for ISO-like strings.
 *
 * @param {string} str - Date string, e.g. "22.08.2026".
 * @returns {Date|null} Parsed date or null when invalid/falsy.
 */
export function parseDmyDate(str) {
  if (!str) return null;
  const parts = str.split('.');
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a promo's "DD.MM.YYYY" start/end dates into a short display range,
 * e.g. "Янв 1 - Янв 14".
 *
 * @param {string} startStr - Promo start date, "DD.MM.YYYY".
 * @param {string} endStr - Promo end date, "DD.MM.YYYY".
 * @returns {string} Formatted range, or just one side when the other is missing.
 */
export function formatPromoDateRange(startStr, endStr) {
  const monthNames = [
    "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
    "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"
  ];
  const format = (str) => {
    const d = parseDmyDate(str);
    return d ? `${monthNames[d.getMonth()]} ${d.getDate()}` : "";
  };

  const start = format(startStr);
  const end = format(endStr);
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

/**
 * Convert an ISO date ("YYYY-MM" or "YYYY-MM-DD") to "DD.MM.YYYY" for the
 * promo endpoint. When the day is absent, the result anchors to the 1st, or
 * to the last day of the month when `endOfMonth` is true.
 *
 * @param {string} isoStr - ISO date string.
 * @param {boolean} [endOfMonth=false] - Anchor a day-less value to month end.
 * @returns {string} "DD.MM.YYYY" string (input returned unchanged when falsy/invalid).
 */
export function isoToDmy(isoStr, endOfMonth = false) {
  if (!isoStr) return isoStr;
  const parts = isoStr.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  if (!year || !month) return isoStr;
  const day = parts[2]
    ? Number(parts[2])
    : (endOfMonth ? new Date(year, month, 0).getDate() : 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(day)}.${pad(month)}.${year}`;
}

/**
 * Map a date string to a 0-based week column index relative to the first
 * upcoming month. Each month is split into 4 "weeks" by day-of-month.
 *
 * @param {string} dateStr - "DD.MM.YYYY" (or ISO) date string.
 * @param {Date} baseDate - First day of the first visible month.
 * @returns {number|null} Week column index, or null when unparseable.
 */
export function getWeekIndexForDate(dateStr, baseDate) {
  const d = parseDmyDate(dateStr);
  if (!d) return null;
  const monthOffset =
    (d.getFullYear() - baseDate.getFullYear()) * 12 + (d.getMonth() - baseDate.getMonth());
  const weekInMonth = Math.min(3, Math.floor((d.getDate() - 1) / 7));
  return monthOffset * 4 + weekInMonth;
}

/**
 * Compute the clamped [start, end] week window a promo occupies, or null when
 * it falls entirely outside the visible month range.
 *
 * @param {Object} promo - Promo with `promo_start_date` / `promo_end_date`.
 * @param {Date} baseDate - First day of the first visible month.
 * @param {number} [totalWeeks=PROMO_TOTAL_WEEKS] - Number of visible week columns.
 * @returns {{start: number, end: number}|null} Clamped week range or null.
 */
export function getPromoWeekRange(promo, baseDate, totalWeeks = PROMO_TOTAL_WEEKS) {
  let start = getWeekIndexForDate(promo.promo_start_date, baseDate);
  let end = getWeekIndexForDate(promo.promo_end_date, baseDate);
  if (start == null && end == null) return null;
  if (start == null) start = end;
  if (end == null) end = start;
  if (end < 0 || start > totalWeeks - 1) return null;
  start = Math.max(0, start);
  end = Math.min(totalWeeks - 1, end);
  if (start > end) return null;
  return { start, end };
}